import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignInOptions } from "@/components/SignInOptions";
import { fetchCurrentUser, useAuth, verifyMagicLink } from "@/lib/auth";

// Module-level so React StrictMode's double effect run reuses the same in-flight verify (tokens are single-use).
const inflight = new Map();
const verifyOnce = (token) => {
  if (!inflight.has(token)) inflight.set(token, verifyMagicLink(token));
  return inflight.get(token);
};

export default function MagicLinkPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const token = new URLSearchParams(location.search).get("token");
    if (!token) { setError("This sign-in link is missing its code. Request a new one below."); return undefined; }
    verifyOnce(token)
      .then(({ next, session_token, ...user }) => {
        if (!active) return;
        setUser(user);
        toast.success(`Signed in as ${user.email}`, { id: "magic-signed-in" });
        navigate(next && next.startsWith("/") ? next : "/#jobs", { replace: true });
      })
      .catch(async (err) => {
        // Link already consumed (reload, prefetch, double navigation) but the session cookie is set: carry on.
        const existing = await fetchCurrentUser().catch(() => null);
        if (!active) return;
        if (existing) {
          setUser(existing);
          toast.success(`Signed in as ${existing.email}`, { id: "magic-signed-in" });
          navigate("/#jobs", { replace: true });
          return;
        }
        setError(err.message);
      });
    return () => { active = false; };
  }, [location.search, navigate, setUser]);

  return (
    <main data-testid="magic-link-page" className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-md">
        <img src="/dcyb-logo-horizontal.png" alt="DCYB" className="h-12 w-auto mb-8" />
        {error ? (
          <div data-testid="magic-link-error" role="alert" className="space-y-5">
            <div>
              <p className="font-display text-2xl font-extrabold text-[#0F141B]">That link didn't work.</p>
              <p data-testid="magic-link-error-text" className="mt-2 text-sm text-[#3D4049] leading-relaxed">{error}</p>
            </div>
            <SignInOptions idPrefix="magic-retry" next="/#jobs" />
            <Link to="/" data-testid="magic-link-home" className="inline-block text-xs underline underline-offset-4 text-[#5F6168]">Back to DCYB</Link>
          </div>
        ) : (
          <div data-testid="magic-link-verifying" role="status" className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#5F6168] flex items-center gap-3"><span className="live-dot" aria-hidden />Signing you in</div>
        )}
      </div>
    </main>
  );
}
